'use client';

import { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@/i18n/navigation';
import { MapPinIcon, CheckIcon, CreditCardIcon, PlusIcon, PackageCheckIcon, ShoppingBagIcon, ClipboardListIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PriceDisplay } from '@/components/partials';
import AddressDialog from '@/components/partials/registered/addresses/address-dialog';
import { listAddresses, checkout } from '@/lib/api';
import { cn, getErrorMessage } from '@/lib/utils';
import { useCartStore } from '@/store';
import { useMetadataStore } from '@/store';
import { useAppLocale, useTranslatedRoute } from '@/hooks';
import { AddressType, AddressesListResponse } from '@/types/address';
type PropsType = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};
const CheckoutDialog = ({
  open,
  onOpenChange
}: PropsType) => {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const queryClient = useQueryClient();
  const {
    cartState,
    getCart
  } = useCartStore();
  const {
    backendLocale
  } = useAppLocale();
  const {
    buildRoute
  } = useTranslatedRoute();
  const metadata = useMetadataStore(state => state.metadata);
  const {
    data: addressData,
    isLoading: addressesLoading
  } = useQuery<AddressesListResponse>({
    queryKey: ['addresses-checkout'],
    queryFn: () => listAddresses({
      size: 100
    }),
    enabled: open
  });
  const addresses = addressData?.items ?? [];
  const primaryAddress = addresses.find((a: AddressType) => a.primary) ?? addresses[0] ?? null;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [contractAccepted, setContractAccepted] = useState(false);
  const [contractOpen, setContractOpen] = useState(false);
  const [addAddressOpen, setAddAddressOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [placedOrderId, setPlacedOrderId] = useState<string | null>(null);
  const effectiveSelected = selectedId ?? primaryAddress?.id ?? null;
  const items = cartState.response?.items ?? [];
  const total = cartState.response?.total;
  const distanceSellingContent = metadata?.contentSettings?.distanceSellingContract?.[backendLocale] ?? '';
  const formatAddress = (a_0: AddressType) => {
    const parts = [a_0.text, a_0.city?.name, a_0.state?.name, a_0.country?.name, a_0.zipCode].filter(Boolean);
    return parts.join(', ');
  };
  const getItemTitle = (item: (typeof items)[number]) => {
    const translations = item.product.translations;
    const localeKey = Object.keys(translations)[0];
    return localeKey ? translations[localeKey]?.title ?? item.product.code : item.product.code;
  };
  const canPlaceOrder = !!effectiveSelected && contractAccepted && !placing;

  // Reset form state only after the dialog close animation completes
  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setOrderPlaced(false);
        setSelectedId(null);
        setNotes('');
        setContractAccepted(false);
        setPlacedOrderId(null);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);
  const handleAddressAdded = async (newAddress: AddressType) => {
    await queryClient.invalidateQueries({
      queryKey: ['addresses-checkout']
    });
    setSelectedId(newAddress.id);
  };
  const handlePlaceOrder = async () => {
    if (!effectiveSelected) {
      return;
    }
    setPlacing(true);
    try {
      const order = await checkout({
        addressId: effectiveSelected,
        notes: notes.trim() || undefined,
        locale
      });
      setPlacedOrderId(order.id);
      setOrderPlaced(true);
      await getCart(); // fire-and-forget — refresh cart in background after success screen is shown
    } catch (error: any) {
      toast.error(getErrorMessage(error, t('something_went_wrong')));
    } finally {
      setPlacing(false);
    }
  };
  const handleClose = () => {
    onOpenChange(false);
    // State is reset via useEffect after close animation (300ms)
  };
  return <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-lg">
          {orderPlaced ? (/* ── Success screen ── */
        <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8 py-12 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                <PackageCheckIcon className="h-10 w-10 text-green-600" />
              </div>
              <div className="flex flex-col gap-2">
                <h2 className="text-xl font-bold text-gray-900">{t('order_placed_success_title')}</h2>
                <p className="text-muted-foreground text-sm">{t('order_placed_success_desc')}</p>
                {placedOrderId && <p className="text-muted-foreground mt-1 font-mono text-xs">
                    {t('order_id')}: {placedOrderId}
                  </p>}
              </div>
              <div className="flex w-full flex-col gap-3 pt-2">
                <Button className="w-full gap-2 bg-green-600 text-white hover:bg-green-700" onClick={() => {
              handleClose();
              router.push(buildRoute('orders'));
            }}>
                  <ClipboardListIcon className="h-4 w-4" />
                  {t('go_to_orders')}
                </Button>
                <Button variant="outline" className="w-full gap-2" onClick={() => {
              handleClose();
              router.push(buildRoute('products'));
            }}>
                  <ShoppingBagIcon className="h-4 w-4" />
                  {t('continue_shopping')}
                </Button>
              </div>
            </div>) : <>
              <DialogHeader className="px-6 pt-6 pb-4">
                <DialogTitle className="flex items-center gap-2">
                  <CreditCardIcon className="text-muted-foreground h-5 w-5" />
                  {t('checkout')}
                </DialogTitle>
              </DialogHeader>

              <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(90vh-9rem)] px-6 pb-4">
                <div className="flex flex-col gap-6">
                  {/* Shipping address */}
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900">
                        <MapPinIcon className="h-4 w-4" />
                        {t('shipping_address')}
                      </h3>
                      <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setAddAddressOpen(true)}>
                        <PlusIcon className="h-3 w-3" />
                        {t('add_address')}
                      </Button>
                    </div>

                    {addressesLoading ? <div className="flex flex-col gap-2">
                        <Skeleton className="h-16 w-full rounded-lg" />
                        <Skeleton className="h-16 w-full rounded-lg" />
                      </div> : addresses.length === 0 ? <p className="text-destructive text-sm">{t('no_addresses_for_checkout')}</p> : <div className="flex flex-col gap-2">
                        {addresses.map((address: AddressType) => {
                    const isSelected = effectiveSelected === address.id;
                    return <button key={address.id} type="button" onClick={() => setSelectedId(address.id)} className={cn('flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors', isSelected ? 'border-brand bg-brand/5' : 'border-border hover:border-brand/50 hover:bg-muted/30')}>
                              <div className={cn('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors', isSelected ? 'border-brand bg-brand' : 'border-muted-foreground')}>
                                {isSelected && <CheckIcon className="h-2.5 w-2.5 text-white" />}
                              </div>
                              <div className="flex flex-1 flex-col gap-0.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900">{address.title}</span>
                                  {address.primary && <Badge variant="outline" className="px-1 py-0 text-[10px]">
                                      {t('primary')}
                                    </Badge>}
                                </div>
                                {formatAddress(address) && <span className="text-muted-foreground text-xs leading-snug">
                                    {formatAddress(address)}
                                  </span>}
                              </div>
                            </button>;
                  })}
                      </div>}
                  </div>

                  <Separator />

                  {/* Order summary */}
                  <div className="flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-gray-900">{t('order_summary')}</h3>
                    <ScrollArea viewportClassName="max-h-48">
                      <div className="flex flex-col gap-2 pr-3">
                        {items.map(item_0 => <div key={item_0.id} className="flex items-center justify-between gap-3 text-sm">
                            <span className="min-w-0 flex-1">
                              <span className="text-muted-foreground block text-[10px]">{item_0.product.code}</span>
                              <span className="line-clamp-1 text-gray-700">
                                {getItemTitle(item_0)}
                                <span className="text-muted-foreground ml-1">x{item_0.quantity}</span>
                              </span>
                            </span>
                            {item_0.itemTotal && <PriceDisplay gross={item_0.itemTotal.gross} originalGross={item_0.itemTotal.originalGross} discountRate={item_0.itemTotal.discountRate} currencyCode={item_0.itemTotal.currencyCode} locale={backendLocale} size="sm" />}
                          </div>)}
                      </div>
                    </ScrollArea>

                    {total && <>
                        <Separator />
                        <div className="flex flex-col gap-1">
                          {total.originalGross !== null && total.originalGross !== undefined && total.originalGross !== total.gross && <div className="flex items-center justify-between">
                                <span className="text-xs text-green-700">{t('discount')}</span>
                                <span className="text-xs font-medium text-green-700">
                                  -
                                  {new Intl.NumberFormat(backendLocale, {
                          style: 'currency',
                          currency: total.currencyCode,
                          minimumFractionDigits: 2
                        }).format(total.originalGross - total.gross)}
                                </span>
                              </div>}
                          {total.taxAmount > 0 && <div className="flex items-center justify-between">
                              <span className="text-muted-foreground text-xs">{t('tax')}</span>
                              <span className="text-xs text-gray-600">
                                {new Intl.NumberFormat(backendLocale, {
                          style: 'currency',
                          currency: total.currencyCode,
                          minimumFractionDigits: 2
                        }).format(total.taxAmount)}
                              </span>
                            </div>}
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="text-xs text-gray-500">{t('total')}</span>
                            <PriceDisplay gross={total.gross} originalGross={total.originalGross} discountRate={total.discountRate} currencyCode={total.currencyCode} locale={backendLocale} size="md" discountedClassName="text-base font-semibold" />
                          </div>
                        </div>
                      </>}
                  </div>

                  <Separator />

                  {/* Order notes */}
                  <div className="flex flex-col gap-2">
                    <label htmlFor="checkout-notes" className="text-sm font-semibold text-gray-900">
                      {t('order_notes')}
                      <span className="text-muted-foreground ml-1 font-normal">({t('optional')})</span>
                    </label>
                    <Textarea id="checkout-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('order_notes_placeholder')} className="resize-none text-sm" rows={3} maxLength={500} />
                  </div>

                  <Separator />

                  {/* Distance selling contract */}
                  <div className="flex cursor-pointer items-start gap-2" onClick={() => setContractAccepted(prev => !prev)}>
                    <Checkbox checked={contractAccepted} onCheckedChange={val => setContractAccepted(!!val)} className="pointer-events-none mt-0.5" />
                    <span className="text-sm leading-snug text-gray-700 select-none">
                      {t.rich('i_have_read_and_accept_distance_selling', {
                    link: chunks => <button type="button" onClick={e_0 => {
                      e_0.stopPropagation();
                      setContractOpen(true);
                    }} className="text-brand font-medium hover:underline">
                            {chunks}
                          </button>
                  })}
                    </span>
                  </div>
                </div>
              </ScrollArea>

              <DialogFooter className="border-t px-6 py-4">
                <Button variant="outline" onClick={handleClose} disabled={placing}>
                  {t('cancel')}
                </Button>
                <Button className="gap-2 bg-green-600 text-white hover:bg-green-700" disabled={!canPlaceOrder} onClick={handlePlaceOrder}>
                  <CreditCardIcon className="h-4 w-4" />
                  {placing ? t('placing_order') : t('place_order')}
                </Button>
              </DialogFooter>
            </>}
        </DialogContent>
      </Dialog>

      {/* Add address dialog */}
      <AddressDialog open={addAddressOpen} onOpenChange={setAddAddressOpen} onSuccess={handleAddressAdded} />

      {/* Distance selling contract viewer */}
      <Dialog open={contractOpen} onOpenChange={setContractOpen}>
        <DialogContent aria-describedby={undefined} className="max-h-[85vh] sm:max-w-2xl">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>{t('distance_selling_contract')}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1" viewportClassName="max-h-[calc(85vh-5rem)] px-6 py-5">
            <div className="prose prose-sm max-w-none pb-1 text-sm leading-relaxed whitespace-pre-wrap">
              {distanceSellingContent || <span className="text-muted-foreground">{t('no_content')}</span>}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>;
};
export default CheckoutDialog;